import {
  CUSTOM_SCHEDULE_ATTRIBUTE,
  PARTNER_BUSINESS_HOURS_ATTRIBUTE,
  PARTNER_BUSINESS_HOURS_OPTIONS,
  POLICY_TIME_CONDITION_ATTRIBUTES,
  type PolicyTimeConditionAttribute,
  type TimeWindow,
} from '@provision/sidekick-utils';
import type { Operator } from 'react-querybuilder';
import { buildDefaultScheduleWindows } from 'src/components/ScheduleConditionEditor';
import {
  ConditionType,
  DataConditionOperator,
  FormControlType,
} from 'src/graphql/entities/behavior/behavior-config/behavior-config.types';
import {
  BehaviorConfigOption,
  RuleControlType,
} from 'src/graphql/entities/behavior/behavior.types';
import type { SupportPolicyConditionField } from './support-policy.conditions';

/** Section header in the attribute picker — the same one the behaviors rules builder uses. */
export const TIME_CONDITION_GROUP_LABEL = 'Hours';

const PARTNER_BUSINESS_HOURS_FIELD_OPTIONS: BehaviorConfigOption[] =
  PARTNER_BUSINESS_HOURS_OPTIONS.map((option) => ({
    id: option.value,
    value: option.value,
    label: option.label,
  }));

const OPERATORS_BY_TIME_ATTRIBUTE: Record<PolicyTimeConditionAttribute, Operator[]> = {
  [PARTNER_BUSINESS_HOURS_ATTRIBUTE]: [{ name: DataConditionOperator.Equals, label: 'Equal' }],
  // nothing to compare against; the one operator reads as the row's label:
  // "Applies on [days] from [time] to [time]"
  [CUSTOM_SCHEDULE_ATTRIBUTE]: [{ name: DataConditionOperator.In, label: 'Applies on' }],
};

/**
 * The two Hours conditions. Both carry `uiType: List`: Partner Business really is one (Open /
 * Closed), while a Custom schedule row is rendered by the policy value editor from its attribute
 * before the `uiType` is consulted.
 */
export const TIME_CONDITION_FIELDS: SupportPolicyConditionField[] =
  POLICY_TIME_CONDITION_ATTRIBUTES.map(({ attribute, label }) => {
    const options =
      attribute === PARTNER_BUSINESS_HOURS_ATTRIBUTE
        ? PARTNER_BUSINESS_HOURS_FIELD_OPTIONS
        : [];

    return {
      name: attribute,
      value: attribute,
      attribute,
      label,
      group: TIME_CONDITION_GROUP_LABEL,
      uiType: FormControlType.List,
      controlType: RuleControlType.ConditionControl,
      conditionType: ConditionType.Data,
      operators: OPERATORS_BY_TIME_ATTRIBUTE[attribute],
      options,
      fieldOptions: options,
      triggers: [],
    };
  });

/**
 * What a freshly picked Hours condition starts with, so the row is complete before the user touches
 * it: Open, or the escalation editor's default custom schedule (weekdays 09:00–17:00). Every other
 * attribute keeps react-querybuilder's own default.
 */
export function getTimeConditionDefaultValue(
  attribute: unknown,
): BehaviorConfigOption | TimeWindow[] | undefined {
  if (attribute === PARTNER_BUSINESS_HOURS_ATTRIBUTE) {
    return PARTNER_BUSINESS_HOURS_FIELD_OPTIONS[0];
  }

  if (attribute === CUSTOM_SCHEDULE_ATTRIBUTE) {
    return buildDefaultScheduleWindows();
  }

  return undefined;
}
